/**
 * CRM Auth Context — siloed from marketplace auth.
 * Stores token in localStorage under a separate key.
 */
import { createContext, useContext, useState, useEffect } from "react";
import { API } from "@/App";

const CRMAuthContext = createContext(null);

const TOKEN_KEY = "crm_auth_token";

export const CRMAuthProvider = ({ children }) => {
  const [crmUser, setCrmUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;

  const fetchMe = async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      setCrmUser(null);
      setOrganization(null);
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API}/crm/auth/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCrmUser({
          crm_user_id: data.crm_user_id,
          email: data.email,
          name: data.name,
          role: data.role,
          crm_role: data.crm_role,
          account_type: data.account_type,
          org_id: data.org_id,
        });
        setOrganization(data.organization);
      } else {
        localStorage.removeItem(TOKEN_KEY);
        setCrmUser(null);
        setOrganization(null);
      }
    } catch {
      setCrmUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMe();
    // eslint-disable-next-line
  }, []);

  const loginCRM = async (email, password) => {
    const res = await fetch(`${API}/crm/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Login failed");
    }
    const data = await res.json();
    localStorage.setItem(TOKEN_KEY, data.token);
    await fetchMe();
    return data;
  };

  const registerPersonal = async (payload) => {
    const res = await fetch(`${API}/crm/auth/register/personal`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Registration failed");
    }
    const data = await res.json();
    localStorage.setItem(TOKEN_KEY, data.token);
    await fetchMe();
    return data;
  };

  const registerBusiness = async (payload) => {
    const res = await fetch(`${API}/crm/auth/register/business`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Registration failed");
    }
    const data = await res.json();
    localStorage.setItem(TOKEN_KEY, data.token);
    await fetchMe();
    return data;
  };

  const acceptInvitation = async (payload) => {
    const res = await fetch(`${API}/crm/auth/seats/accept-invitation`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Failed to accept invitation");
    }
    const data = await res.json();
    localStorage.setItem(TOKEN_KEY, data.token);
    await fetchMe();
    return data;
  };

  const logoutCRM = async () => {
    try {
      await fetch(`${API}/crm/auth/logout`, { method: "POST" });
    } catch { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    setCrmUser(null);
    setOrganization(null);
  };

  const refreshOrg = fetchMe;
  const getToken = () => localStorage.getItem(TOKEN_KEY);

  return (
    <CRMAuthContext.Provider
      value={{
        crmUser,
        organization,
        loading,
        token,
        loginCRM,
        registerPersonal,
        registerBusiness,
        acceptInvitation,
        logoutCRM,
        refreshOrg,
        getToken,
      }}
    >
      {children}
    </CRMAuthContext.Provider>
  );
};

export const useCRMAuth = () => {
  const ctx = useContext(CRMAuthContext);
  if (!ctx) throw new Error("useCRMAuth must be inside CRMAuthProvider");
  return ctx;
};
